import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, X, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const SESSION_KEY = "jglinks_ai_session";

const SUGGESTIONS = [
  "Dame todos los links de OnlyFans de las modelos",
  "Resumen del autoposting",
  "¿Cuántos leads tiene cada modelo?",
  "Programa 6 broadcasts al día en VIP",
  "Muéstrame los posts fallidos de la última semana",
  "¿Cuántas solicitudes VIP están pendientes?",
];

const Bubble = ({ role, children }) => {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-sm ${
          isUser
            ? "bg-white text-black rounded-br-sm"
            : "bg-white/10 text-white border border-white/10 rounded-bl-sm"
        }`}
        data-testid={`ai-msg-${role}`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{children}</p>
        ) : (
          <div className="prose prose-sm prose-invert max-w-none [&>*]:text-white/90 [&_a]:text-pink-300 [&_a]:underline [&_strong]:text-white [&_code]:text-pink-200 [&_code]:bg-black/30 [&_code]:px-1 [&_code]:rounded [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0">
            <ReactMarkdown>{children}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminAIAssistant = () => {
  const { getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return localStorage.getItem(SESSION_KEY) || "";
    } catch (_) {
      return "";
    }
  });
  const scrollRef = useRef(null);

  const persistSession = (id) => {
    try {
      localStorage.setItem(SESSION_KEY, id);
    } catch (_) {}
  };

  const scrollDown = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 50);
  }, []);

  useEffect(() => {
    if (open) scrollDown();
  }, [open, messages.length, scrollDown]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      // Build URL — use absolute backend URL to avoid relative-resolution issues
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${baseUrl}/api/admin/ai-assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ sessionId, message: msg }),
        // 90s — assistant may iterate through multiple tools
        signal: AbortSignal.timeout
          ? AbortSignal.timeout(120000)
          : undefined,
      });
      const ct = r.headers.get("content-type") || "";
      if (!r.ok || !ct.includes("application/json")) {
        if (r.status === 404 || (await r.text()).startsWith("<!DOCTYPE")) {
          setMessages((m) => [
            ...m,
            {
              role: "assistant",
              content:
                "❌ El asistente IA todavía no está deployado en este entorno. Haz **Deploy** en Emergent para activarlo.",
            },
          ]);
        } else if (r.status === 401 || r.status === 403) {
          setMessages((m) => [
            ...m,
            { role: "assistant", content: "❌ Sesión admin expirada — vuelve a iniciar sesión." },
          ]);
        } else {
          setMessages((m) => [
            ...m,
            { role: "assistant", content: `❌ Error HTTP ${r.status}` },
          ]);
        }
        return;
      }
      const d = await r.json();
      if (!sessionId && d.sessionId) {
        setSessionId(d.sessionId);
        persistSession(d.sessionId);
      }
      setMessages((m) => [...m, { role: "assistant", content: d.answer || "(sin respuesta)" }]);
    } catch (e) {
      const reason =
        e?.name === "TimeoutError" || e?.name === "AbortError"
          ? "La consulta tardó demasiado (>2 min). Reformúlala más simple."
          : `Error de red: ${e?.message || "desconocido"}. Asegúrate de haber deployado el último cambio.`;
      setMessages((m) => [...m, { role: "assistant", content: `❌ ${reason}` }]);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    if (sessionId) {
      try {
        await fetch(`/api/admin/ai-assistant/chat/${sessionId}`, {
          method: "DELETE",
          headers: { ...getAuthHeaders() },
        });
      } catch (_) {}
    }
    setMessages([]);
    setSessionId("");
    persistSession("");
    toast.success("Conversación limpiada");
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-launcher"
        title="Asistente IA"
        className={`fixed z-30 bottom-5 right-5 w-12 h-12 rounded-full shadow-2xl shadow-pink-500/40 flex items-center justify-center transition-all opacity-80 hover:opacity-100 ${
          open
            ? "bg-white text-black"
            : "bg-gradient-to-br from-purple-500 to-pink-500 text-white hover:scale-110"
        }`}
      >
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-30 bottom-20 right-5 w-[min(95vw,420px)] h-[min(80vh,640px)] bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="ai-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-purple-500/20 to-pink-500/20">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm">Asistente JGlinks</h3>
                <p className="text-white/50 text-[10px]">
                  Pregunta o pídeme acciones
                </p>
              </div>
            </div>
            {messages.length > 0 && (
              <button
                onClick={reset}
                data-testid="ai-reset"
                title="Limpiar conversación"
                className="p-1.5 text-white/60 hover:text-white rounded hover:bg-white/10"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3"
            data-testid="ai-messages"
          >
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-white/70 text-sm">
                  ¡Hola! Soy tu asistente. Puedo darte info Y ejecutar acciones
                  dentro del CRM. Prueba con:
                </p>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`ai-suggestion-${i}`}
                      className="w-full text-left px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-white/80 hover:text-white transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role}>
                {m.content}
              </Bubble>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/10 border border-white/10 rounded-2xl rounded-bl-sm px-3.5 py-2 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-white/60 animate-spin" />
                  <span className="text-white/60 text-xs">Pensando...</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-white/10 bg-black/30">
            <div className="flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Pregunta o pide una acción..."
                disabled={sending}
                data-testid="ai-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30 resize-none placeholder:text-white/30"
                style={{ minHeight: "38px", maxHeight: "100px" }}
              />
              <button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                data-testid="ai-send"
                className="p-2.5 bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-lg disabled:opacity-30 hover:opacity-90"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAIAssistant;
